import {
    AGENT_STATUSES,
    CAMPAIGN_STATUSES,
} from '../../../../common/enums/enums';
import {
    Badge,
    Box,
    Flex,
    Group,
    Progress,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import { getEnumColor, getEnumLabel } from '../../../../../utils/enumUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { nFormatter } from '../../../../../utils/text';

const AgentStatusProgress = ({ assignment }) => {
    const validAgentStatus = ['None', 'Connected'];

    // show agent status
    const status = assignment.status;
    const agentName = assignment.agentName;
    const agentStatus = assignment.agentStatus;
    //const campaignStatus = assignment.currentCampaignStatus;
    const campaignStatus = assignment.activityStatus;

    if (!validAgentStatus.includes(agentStatus)) {
        const agentStatusLabel = getEnumLabel(AGENT_STATUSES, status);
        const agentStatusColor = getEnumColor(AGENT_STATUSES, status);
        return (
            <Tooltip
                label={
                    <Group gap={2}>
                        <Text size={'sm'}>{agentName}</Text>
                        <Badge
                            size={'xs'}
                            color={agentStatusColor}>
                            {agentStatusLabel}
                        </Badge>
                    </Group>
                }>
                <Progress
                    size='xs'
                    miw={10}
                    color={agentStatusColor}
                    value={100}
                    transitionDuration={0}
                />
            </Tooltip>
        );
    }

    const campaignStatusLabel =
        getEnumLabel(CAMPAIGN_STATUSES, campaignStatus) || 'Unknown';
    const campaignStatusColor = getEnumColor(CAMPAIGN_STATUSES, campaignStatus);

    const showLoader = {
        Initializing: true,
        Provisioning: true,
        Stopping: true,
    };

    return (
        <Tooltip
            label={
                <Group gap={2}>
                    <Text
                        span
                        size={'sm'}>
                        {agentName}
                    </Text>
                    <Badge
                        size={'xs'}
                        color={campaignStatusColor}>
                        {campaignStatusLabel}
                        {showLoader[campaignStatus] ? (
                            <FontAwesomeIcon
                                spin
                                icon={faSpinnerThird}
                            />
                        ) : null}
                    </Badge>
                </Group>
            }>
            <Progress
                size='xs'
                miw={10}
                color={campaignStatusColor}
                value={100}
                transitionDuration={0}
            />
        </Tooltip>
    );
};

/*const test_agents = [
    { name: 'agent-01', status: 'Connected', campaignStatus: 'Running' },
    { name: 'agent-01', status: 'Connected', campaignStatus: 'None' },
    { name: 'agent-02', status: 'Connected', campaignStatus: 'None' },
    { name: 'agent-03', status: 'Connected', campaignStatus: 'None' },
    { name: 'agent-04', status: 'Unhealthy', campaignStatus: 'None' },
    { name: 'agent-05', status: 'ContainerUnhealthy', campaignStatus: null },
    { name: 'agent-06', status: 'Disconnected', campaignStatus: 'None' },
];*/

const StatusCell = ({ cell }) => {
    const { column, row } = cell;
    const rowData = row.original;

    const thresholds = {
        'green.7': 80,
        'teal.6': 70,
        'yellow.6': 35,
        'red': 10,
        'red': 0,
    };

    const campaignBadgeColor = getEnumColor(CAMPAIGN_STATUSES, rowData.status);
    const status = getEnumLabel(CAMPAIGN_STATUSES, rowData.status);

    /*const queueState = {
        loaded: 100,
        remaining: 5000,
        total: 12000,
        totalScrubbed: 1200,
        hasMore: true,
        isLoading: false,
        stats: {
            sendThrottle: 0,
            suppressed: 0,
        },
    };*/
    let { queueState, builderState } = rowData.queue;
    if (!builderState) builderState = { status: 'None', total: 0 };
    if (!queueState)
        queueState = {
            buffered: 0,
            remaining: builderState.total,
            total: builderState.total,
        };

    const value =
        queueState.remaining > 0
            ? parseInt((queueState.remaining / builderState.total) * 100)
            : 0;

    const indexColor = Object.keys(thresholds).find((x) => {
        const thresholdValue = thresholds[x];
        return value >= thresholdValue;
    });

    const totalRemaining = queueState.buffered + queueState.remaining;
    const totalItems = builderState.total;

    const showLoader = {
        Scheduled: true,
        Provisioning: true,
        Initializing: true,
        Stopping: true,
    };

    const hideQueue = {
        Complete: true,
    };

    return (
        <Group gap={'xs'}>
            <Stack
                gap={4}
                miw={100}>
                <Group
                    align={'center'}
                    justify='center'
                    gap={'xs'}>
                    <Badge
                        color={campaignBadgeColor}
                        size={'xs'}
                        py={0}>
                        <Group gap={4}>
                            {status}
                            {showLoader[rowData.status] ? (
                                <FontAwesomeIcon
                                    spin
                                    icon={faSpinnerThird}
                                />
                            ) : null}
                        </Group>
                    </Badge>
                </Group>

                <Group
                    grow
                    gap={5}>
                    {rowData.assignments.map((x) => (
                        <AgentStatusProgress
                            key={x.agentName}
                            assignment={x}
                        />
                    ))}
                </Group>

                {hideQueue[rowData.status] ? null : (
                    <Tooltip
                        label={
                            <Text size={'sm'}>
                                {totalRemaining === 0 ? (
                                    <Text
                                        fw={'bold'}
                                        span>
                                        Queue Empty
                                    </Text>
                                ) : (
                                    <Group gap={2}>
                                        <Text span>Queue Size:</Text>
                                        <Text span>
                                            {`${nFormatter(
                                                totalRemaining,
                                                1
                                            )} / ${nFormatter(totalItems, 1)}`}
                                        </Text>
                                    </Group>
                                )}
                            </Text>
                        }>
                        <Progress
                            color={indexColor}
                            value={value}
                            size={'sm'}
                        />
                    </Tooltip>
                )}
            </Stack>
        </Group>
    );
};

const StatusColumn = () => ({
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    minSize: 40,
    maxSize: 60,
    Cell: ({ cell }) => <StatusCell cell={cell} />,
});
export default StatusColumn;
